import React, { useState } from 'react';
import { useGames } from '../../Context/GameContext';
import { useContest } from '../../Context/ContestContext';
import { useAuth } from '../../Context/AuthContext';
import { getMediaUrl } from '../../Services/apiService'; // Import helper
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Play, Trash2, Image as ImageIcon, Edit2, Search, Filter } from 'lucide-react';
import { Button, Card, Row, Col, Badge, Pagination, Form, InputGroup } from 'react-bootstrap';
import ActiveContestsList from '../../Components/ActiveContestsList';

const GameList = () => {
  const { games, deleteGame, setActiveGameId, loading, error } = useGames();
  const { createRoom } = useContest();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Admin tüm oyunları görür; yetkili (bronz aslan+) yalnızca kendi oyunlarını.
  const isAdmin = user?.isAdmin === true || user?.isAdmin === 1;
  const Swal = require('sweetalert2');

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSort, setFilterSort] = useState('NEWEST');
  const itemsPerPage = 8;
  
  React.useEffect(() => {
     setCurrentPage(1);
  }, [searchTerm, filterSort]);

  let processedGames = [...(games || [])];

  // Yetkili kullanıcı yalnızca kendi oluşturduğu oyunları görür (admin hepsini)
  if (!isAdmin) {
      processedGames = processedGames.filter(
          g => String(g.host_id) === String(user?.id_users)
      );
  }

  if (searchTerm.trim()) {
      processedGames = processedGames.filter(g => 
          g.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
          (g.description && g.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
  }

  if (filterSort === 'NEWEST') {
      processedGames.sort((a,b) => b.id - a.id);
  } else if (filterSort === 'OLDEST') {
      processedGames.sort((a,b) => a.id - b.id);
  } else if (filterSort === 'MOST_QUESTIONS') {
      processedGames.sort((a,b) => (b.questions?.length || 0) - (a.questions?.length || 0));
  } else if (filterSort === 'LEAST_QUESTIONS') {
      processedGames.sort((a,b) => (a.questions?.length || 0) - (b.questions?.length || 0));
  }

  const totalPages = Math.ceil(processedGames.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentGames = processedGames.slice(indexOfFirstItem, indexOfLastItem);

  const handleDelete = (gameId) => {
      Swal.fire({
          title: 'Emin misiniz?',
          text: "Bu oyunu silmek istediğinize emin misiniz? Bu işlem geri alınamaz!",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Evet, sil!',
          cancelButtonText: 'İptal'
      }).then((result) => {
          if (result.isConfirmed) {
              deleteGame(gameId);
              Swal.fire(
                  'Silindi!',
                  'Oyun başarıyla silindi.',
                  'success'
              );
          }
      });
  };

  const handleStartGame = (gameId) => {
     setActiveGameId(gameId);
     navigate('/lobby');
  };

  if (loading) return <div className="text-center py-5">Yükleniyor...</div>;
  if (error) return <div className="alert alert-danger m-4">{error}</div>;

  return (
    <div className="py-2">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">Oyunlar</h2>
          <p className="text-muted mb-0">Yarışma paketlerinizi yönetin.</p>
        </div>
        <Button 
           variant="primary" 
           className="d-flex align-items-center justify-content-center gap-2 shadow-sm border-0"
           onClick={() => navigate('/games/new')}
        >
           <Plus size={18} />
           Yeni Oyun Oluştur
        </Button>
      </div>

      <ActiveContestsList />

      <Card className="border-0 shadow-sm mb-4">
          <Card.Body className="p-3 bg-light rounded-3">
              <Row className="g-3">
                  <Col md={8}>
                      <InputGroup>
                          <InputGroup.Text className="bg-white border-end-0">
                              <Search size={18} className="text-muted"/>
                          </InputGroup.Text>
                          <Form.Control
                              placeholder="Oyun adı veya açıklama ile ara..."
                              className="border-start-0 shadow-none"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                          />
                      </InputGroup>
                  </Col>
                  <Col md={4}>
                      <InputGroup>
                          <InputGroup.Text className="bg-white border-end-0">
                              <Filter size={18} className="text-muted"/>
                          </InputGroup.Text>
                          <Form.Select 
                              className="border-start-0 shadow-none cursor-pointer"
                              value={filterSort}
                              onChange={(e) => setFilterSort(e.target.value)}
                          >
                              <option value="NEWEST">En Yeni</option>
                              <option value="OLDEST">En Eski</option>
                              <option value="MOST_QUESTIONS">En Çok Soru</option>
                              <option value="LEAST_QUESTIONS">En Az Soru</option>
                          </Form.Select>
                      </InputGroup>
                  </Col>
              </Row>
          </Card.Body>
      </Card>

      <Row className="g-4">
        {currentGames.map((game, idx) => (
          <Col xs={12} md={6} lg={4} xl={3} key={game.id}>
            <motion.div
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ delay: idx * 0.1 }}
            >
              <Card className="custom-card h-100 overflow-hidden border-0">
                 <div className="position-relative" style={{height: 160}}>
                    {game.image ? (
                        <img src={getMediaUrl(game.image)} alt={game.title} className="w-100 h-100 object-fit-cover" />
                    ) : (
                        <div className="w-100 h-100 bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center">
                           <ImageIcon size={48} className="text-muted opacity-25" />
                        </div>
                    )}
                    <div className="position-absolute top-0 end-0 p-2">
                       <Badge bg="light" text="dark" className="shadow-sm">
                          {game.questions.length} Soru
                       </Badge>
                       {Number(game.joker_limit) > 0 && (
                          <Badge bg="dark" className="shadow-sm ms-1">
                             {game.joker_limit} joker
                          </Badge>
                       )}
                    </div>
                 </div>
                 
                 <Card.Body className="d-flex flex-column">
                    <h5 className="fw-bold text-dark mb-2">{game.title}</h5>
                    <p className="text-muted small mb-4 flex-fill line-clamp-2">
                       {game.description || 'Açıklama yok.'}
                    </p>
                    
                    <div className="d-flex flex-column gap-2">
                       <Button 
                          variant="success" 
                          className="w-100 fw-bold border-0 d-flex align-items-center justify-content-center gap-2"
                          onClick={() => handleStartGame(game.id)}
                       >
                          <Play size={16} fill="currentColor" /> Başlat
                       </Button>
                       <Button 
                          variant="light" 
                          className="w-100 text-primary border-0 d-flex align-items-center justify-content-center gap-2"
                          onClick={() => navigate(`/games/${game.id}`)}
                       >
                          <Edit2 size={16} /> Düzenle
                       </Button>
                       <Button 
                          variant="light" 
                          className="w-100 text-danger border-0 d-flex align-items-center justify-content-center gap-2"
                          onClick={() => handleDelete(game.id)}
                       >
                          <Trash2 size={16} /> Sil
                       </Button>
                    </div>
                 </Card.Body>
              </Card>
            </motion.div>
          </Col>
        ))}
        {currentGames.length === 0 && (
             <Col xs={12} className="text-center py-5 text-muted">
                <p>{isAdmin ? 'Henüz hiç oyun oluşturulmamış.' : 'Henüz kendinize ait bir oyun oluşturmamışsınız.'}</p>
             </Col>
        )}
      </Row>
      {totalPages > 1 && (
          <div className="d-flex justify-content-center p-3 mt-4 border-top">
              <Pagination className="mb-0">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                  <Pagination.Prev onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
                  
                  {[...Array(totalPages)].map((_, idx) => (
                      <Pagination.Item 
                          key={idx + 1} 
                          active={idx + 1 === currentPage}
                          onClick={() => setCurrentPage(idx + 1)}
                      >
                          {idx + 1}
                      </Pagination.Item>
                  ))}

                  <Pagination.Next onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
          </div>
      )}
    </div>
  );
};

export default GameList;
